<template>
  <li class="nav-item dropdown-notification mr-25">
    <a
      href="#"
      class="nav-link"
      @click.prevent="openDrawer"
    >
      <feather-icon
        :badge="unreadBadge"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </a>

    <b-sidebar
      id="app-notification-drawer"
      :visible="isOpen"
      right
      shadow
      backdrop
      no-header
      width="24rem"
      bg-variant="white"
      sidebar-class="sidebar-lg notification-drawer"
      @change="onDrawerChange"
      @shown="onDrawerShown"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="notification-drawer__header d-flex align-items-center px-2 py-1 border-bottom">
          <div class="d-flex align-items-center flex-grow-1 min-w-0">
            <h4 class="notification-title mb-0 mr-75 text-truncate">
              Notifications
            </h4>
            <b-badge
              v-if="unreadCount > 0"
              pill
              variant="light-primary"
            >
              {{ unreadCount }} New
            </b-badge>
          </div>

          <b-button
            v-if="notifications.length"
            v-b-tooltip.hover.bottom="'Clear all'"
            variant="flat-secondary"
            class="btn-icon"
            size="sm"
            title="Clear all"
            aria-label="Clear all"
            :disabled="!notifications.length"
            @click="clearAll"
          >
            <feather-icon
              icon="Trash2Icon"
              size="16"
            />
          </b-button>

          <feather-icon
            class="ml-50 cursor-pointer text-body"
            icon="XIcon"
            size="18"
            title="Close"
            aria-label="Close"
            role="button"
            tabindex="0"
            @click="hide"
            @keydown.enter="hide"
          />
        </div>

        <!-- Filters / actions -->
        <div class="notification-drawer__toolbar d-flex align-items-center justify-content-end px-2 py-50 border-bottom">
          <b-button
            v-b-tooltip.hover.bottom="'Unread only'"
            :variant="unreadOnly ? 'flat-primary' : 'flat-secondary'"
            class="btn-icon"
            size="sm"
            title="Unread only"
            aria-label="Unread only"
            :aria-pressed="unreadOnly ? 'true' : 'false'"
            @click="unreadOnly = !unreadOnly"
          >
            <feather-icon
              icon="MailIcon"
              size="16"
            />
          </b-button>

          <b-button
            v-b-tooltip.hover.bottom="'Mark all as read'"
            variant="flat-secondary"
            class="btn-icon ml-25"
            size="sm"
            title="Mark all as read"
            aria-label="Mark all as read"
            :disabled="!unreadCount"
            @click="markAllRead"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="16"
            />
          </b-button>
        </div>

        <!-- List -->
        <vue-perfect-scrollbar
          :settings="perfectScrollbarSettings"
          class="notification-drawer__list scroll-area"
        >
          <div
            v-if="loading && !notifications.length"
            class="d-flex align-items-center justify-content-center py-3 text-muted"
          >
            <b-spinner
              small
              class="mr-50"
            />
            <small>Loading…</small>
          </div>

          <div
            v-else-if="!filteredNotifications.length"
            class="d-flex flex-column align-items-center justify-content-center py-3 px-2 text-center"
          >
            <feather-icon
              icon="BellOffIcon"
              size="28"
              class="text-muted mb-50"
            />
            <small class="text-muted">
              {{ unreadOnly && notifications.length ? 'No unread notifications' : 'No notifications yet' }}
            </small>
          </div>

          <b-link
            v-for="notification in filteredNotifications"
            :key="notification.id"
            class="d-block notification-drawer__item"
            :class="{ 'notification-unread': !notification.read }"
            @click.prevent="onNotificationClick(notification, hide)"
          >
            <b-media>
              <template #aside>
                <b-avatar
                  size="32"
                  :variant="notification.variant || 'light-primary'"
                >
                  <feather-icon :icon="notification.icon || 'BellIcon'" />
                </b-avatar>
              </template>
              <p class="media-heading d-flex align-items-start mb-25">
                <span class="font-weight-bolder flex-grow-1">
                  {{ notification.title }}
                </span>
                <span
                  v-if="!notification.read"
                  v-b-tooltip.hover.left="'Mark as read'"
                  class="bullet bullet-sm bullet-primary ml-50 mt-25 cursor-pointer"
                  role="button"
                  tabindex="0"
                  @click.stop.prevent="markOneRead(notification)"
                  @keydown.enter.stop.prevent="markOneRead(notification)"
                />
              </p>
              <small class="notification-text d-block">{{ notification.message }}</small>
              <small class="text-muted">{{ formatTime(notification.createdAt) }}</small>
            </b-media>
          </b-link>
        </vue-perfect-scrollbar>
      </template>
    </b-sidebar>
  </li>
</template>

<script>
import {
  BSidebar, BBadge, BMedia, BLink, BAvatar, BButton, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { mapGetters, mapState, mapActions } from 'vuex'
import moment from 'moment'

export default {
  components: {
    BSidebar,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isOpen: false,
      unreadOnly: false,
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
    }
  },
  computed: {
    ...mapState('notifications', ['loading']),
    ...mapGetters('notifications', ['unreadCount']),
    notifications() {
      return this.$store.getters['notifications/items'] || []
    },
    filteredNotifications() {
      if (!this.unreadOnly) return this.notifications
      return this.notifications.filter(n => n && !n.read)
    },
    unreadBadge() {
      const count = this.unreadCount
      if (!count) return null
      return count > 99 ? '99+' : String(count)
    },
  },
  created() {
    this.hydrate()
  },
  methods: {
    ...mapActions('notifications', {
      hydrateStore: 'hydrate',
      refreshStore: 'refresh',
      markReadAction: 'markRead',
      markAllReadAction: 'markAllRead',
      clearAllAction: 'clearAll',
    }),
    hydrate() {
      this.hydrateStore()
    },
    openDrawer() {
      this.isOpen = true
    },
    onDrawerChange(val) {
      this.isOpen = !!val
    },
    onDrawerShown() {
      this.refreshStore()
    },
    formatTime(value) {
      if (!value) return ''
      const m = moment(value)
      if (!m.isValid()) return ''
      if (moment().diff(m, 'days') < 2) return m.fromNow()
      return m.format('DD MMM, h:mm A')
    },
    async markOneRead(notification) {
      if (!notification || notification.read) return
      await this.markReadAction({ id: notification.id })
    },
    async onNotificationClick(notification, hide) {
      if (!notification) return
      if (!notification.read) {
        await this.markReadAction({ id: notification.id })
      }
      if (typeof hide === 'function') hide()
      const link = notification.link
      if (!link) return
      try {
        if (typeof link === 'string') {
          if (link.startsWith('http://') || link.startsWith('https://')) {
            window.open(link, '_blank', 'noopener')
            return
          }
          await this.$router.push(link)
          return
        }
        if (typeof link === 'object') {
          const location = { ...link }
          if (notification.linkQuery && typeof notification.linkQuery === 'object') {
            location.query = { ...(location.query || {}), ...notification.linkQuery }
          }
          await this.$router.push(location)
        }
      } catch (e) {
        // ignore NavigationDuplicated
      }
    },
    async markAllRead() {
      if (!this.unreadCount) return
      await this.markAllReadAction()
    },
    async clearAll() {
      if (!this.notifications.length) return
      await this.clearAllAction()
      // Re-sync from API so dismissed_at rows stay gone across devices.
      try {
        await this.refreshStore()
      } catch (e) { /* ignore */ }
    },
  },
}
</script>

<style lang="scss">
.notification-drawer {
  width: 24rem !important;
  max-width: 100vw;

  .b-sidebar-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
}

.notification-drawer__header {
  flex-shrink: 0;
}

.notification-drawer__toolbar {
  flex-shrink: 0;
}

.notification-drawer__list {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none !important;
  height: 100%;

  .media {
    padding: 0.9rem 1.28rem;
    border: none;
    border-bottom: 1px solid #ebe9f1;
    margin: 0;

    &:hover {
      background: #f8f8f8;
    }
  }

  .media-heading {
    color: #6e6b7b;
    margin-bottom: 0;
    line-height: 1.2;
  }

  .notification-text {
    margin-bottom: 0.35rem;
    font-size: smaller;
    color: #b9b9c3;
  }
}

.notification-drawer__item.notification-unread {
  background-color: rgba(115, 103, 240, 0.04);

  &:hover {
    background-color: rgba(115, 103, 240, 0.08);
  }

  .media:hover {
    background: transparent;
  }
}

.dark-layout {
  .notification-drawer__list {
    .media {
      border-bottom-color: #3b4253;

      &:hover {
        background: #283046;
      }
    }

    .media-heading {
      color: #d0d2d6;
    }
  }

  .notification-drawer__item.notification-unread {
    background-color: rgba(115, 103, 240, 0.12);

    &:hover {
      background-color: rgba(115, 103, 240, 0.18);
    }
  }
}
</style>
